import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { Box } from "@mui/material";

import type { DictationCompleteEvent, Patient } from "@thalamos/aspire-common";

import { TortusDictationSession } from "./TortusDictationSession.js";
import type { DictationInsertMode } from "./TortusResultChooser.js";

export type TortusDictationRequest = {
  patient?: Patient;
  formDraftId?: string;
  /**
   * Form-level mode: skip the per-output chooser and emit the whole note, so the
   * caller can distribute it across the form itself.
   */
  skipChooser?: boolean;
  /**
   * Text already in the field this will land in. When non-empty the chooser asks
   * whether to replace it or add after it. Leave unset where the answer isn't the
   * user's to give — the form-level flow spreads one note over many fields and
   * resolves this per field itself.
   */
  existingText?: string;
  /**
   * Fired once, when the user has settled on the text they want. `insertMode` is
   * always "append" unless the chooser asked and the user chose otherwise.
   */
  onDictationComplete: (
    event: DictationCompleteEvent,
    insertMode: DictationInsertMode,
  ) => void;
};

type TortusDictationContextValue = {
  /**
   * Dock the panel and start a consultation. Only one session runs at a time —
   * requesting again ends the one in progress and starts a fresh one for the new
   * target, rather than redirecting the old consultation's result.
   */
  requestDictation: (request: TortusDictationRequest) => void;
  closeDictation: () => void;
  isOpen: boolean;
};

const TortusDictationContext =
  createContext<TortusDictationContextValue | null>(null);

/**
 * Lets a field or launcher anywhere in the form tree open the shared dictation
 * panel without the page having to thread state down to it.
 */
export function useTortusDictation(): TortusDictationContextValue {
  const context = useOptionalTortusDictation();
  if (context === null) {
    throw new Error(
      "useTortusDictation must be used inside a <TortusDictationProvider>",
    );
  }
  return context;
}

/**
 * As {@link useTortusDictation}, but yields null outside a provider. For form
 * fields, which are reused on pages that host no dictation panel — those offer no
 * dictation rather than failing to render.
 */
export function useOptionalTortusDictation(): TortusDictationContextValue | null {
  return useContext(TortusDictationContext);
}

/** Gap left between the panel and the stuck chrome either side of it. */
const PANEL_STICKY_GAP = 8;

/**
 * Height of one piece of page chrome while it is stuck to an edge of the
 * scrollport, or 0 when it is not stuck there.
 *
 * The patient banner and the form footer both stick at a higher z-index than the
 * docked panel, so a panel spanning the full viewport would have its header and
 * close button hidden under the banner, and the embed's own controls hidden under
 * the footer. Measured rather than hard-coded: both change height with viewport
 * and content, and each is only conditionally sticky — a scrolling one needs no
 * clearance, hence the computed-position check.
 */
function useStuckChromeHeight(testId: string): number {
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const element = document.querySelector(`[data-testid="${testId}"]`);
    if (!(element instanceof HTMLElement)) return;

    // Re-read the position every time rather than once on mount: the footer only
    // sticks once the form is tall enough and the viewport wide enough, so it
    // starts and stops sticking while the page is open. Chrome that is not
    // currently stuck scrolls away and needs no clearance.
    const measure = () => {
      const stuck = getComputedStyle(element).position === "sticky";
      setHeight(stuck ? element.getBoundingClientRect().height : 0);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [testId]);

  return height;
}

/**
 * Hosts the docked Tortus panel and lays it out as a row beside its own
 * children, so the section is squeezed rather than covered. Mount this around
 * just the part of the page that should give up the width — the panel is in
 * normal flow, so it begins wherever this sits rather than at the top of the
 * viewport.
 *
 * On narrow screens the session renders as a dialog instead, which portals out of
 * this row and so takes none of its width.
 */
export function TortusDictationProvider({ children }: { children: ReactNode }) {
  // Sessions are identified as well as stored: the id keys the session component,
  // so a second request while one is open remounts it. Without that the mounted
  // session keeps its already-started consultation and simply adopts the new
  // request's callback — finishing the old recording into the new field.
  const [request, setRequest] = useState<{
    id: number;
    value: TortusDictationRequest;
  } | null>(null);
  const nextRequestId = useRef(0);
  const stuckBannerHeight = useStuckChromeHeight("patient-banner");
  const stuckFooterHeight = useStuckChromeHeight("form-footer");

  const closeDictation = useCallback(() => {
    setRequest(null);
  }, []);

  const value = useMemo<TortusDictationContextValue>(
    () => ({
      requestDictation: (next: TortusDictationRequest) => {
        setRequest({ id: nextRequestId.current++, value: next });
      },
      closeDictation,
      isOpen: request !== null,
    }),
    [closeDictation, request],
  );

  return (
    <TortusDictationContext.Provider value={value}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
        }}
      >
        {/* minWidth: 0 stops the form's own wide content (tables, long labels)
            from refusing to shrink and pushing the panel off the row. */}
        <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
        {request !== null && (
          <TortusDictationSession
            key={request.id}
            open
            onClose={closeDictation}
            stickyTop={stuckBannerHeight + PANEL_STICKY_GAP}
            bottomInset={stuckFooterHeight + PANEL_STICKY_GAP}
            patient={request.value.patient}
            formDraftId={request.value.formDraftId}
            skipChooser={request.value.skipChooser}
            existingText={request.value.existingText}
            onDictationComplete={(event, insertMode) => {
              request.value.onDictationComplete(event, insertMode);
              closeDictation();
            }}
          />
        )}
      </Box>
    </TortusDictationContext.Provider>
  );
}
